import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Navbar } from '../../components/Navbar';
import { Footer } from '../../components/Footer';
import { useAuth } from '../../context/AuthContext';
import { ArrowRight, Calculator, Ruler } from 'lucide-react';

/* Mirrors the backend gutter material formula (server.py calculate_gutters)
   so the free calculator matches what the app produces. */
function calcGutters(linearFt) {
  const L = Number(linearFt) || 0;
  if (L <= 0) return null;
  const sections = Math.floor(L / 10) + (L % 10 > 0 ? 1 : 0);
  const downspouts = Math.max(2, Math.floor(L / 40) + (L % 40 > 15 ? 1 : 0));
  const elbows = downspouts * 2 + 1;
  const runs = Math.max(1, Math.floor(L / 35));
  const endCaps = runs * 2;
  const outsideCorners = Math.max(0, Math.floor(L / 60));
  const insideCorners = Math.max(0, Math.floor(L / 120));
  const hangers = Math.floor(L / 3) + 1;
  const screwsLbs = Math.round(sections * 0.25 * 10) / 10;
  const splashBlocks = downspouts;
  const sealantTubes = Math.max(1, Math.floor(L / 20));
  return { L: Math.round(L * 10) / 10, sections, downspouts, elbows, endCaps, outsideCorners, insideCorners, hangers, screwsLbs, splashBlocks, sealantTubes };
}

export default function GutterCalculator() {
  const { openAuthModal } = useAuth();
  const [mode, setMode] = useState('gutter'); // 'gutter' = eave length, 'perimeter' = house perimeter
  const [value, setValue] = useState('');

  const linearFt = useMemo(() => {
    const v = Number(value) || 0;
    return mode === 'perimeter' ? v * 0.6 : v; // ~60% of perimeter has gutters (eaves only)
  }, [value, mode]);

  const r = useMemo(() => calcGutters(linearFt), [linearFt]);

  const rows = r ? [
    ['Gutter sections (10 ft)', r.sections],
    ['Downspouts', r.downspouts],
    ['Downspout elbows', r.elbows],
    ['End caps', r.endCaps],
    ['Outside corners', r.outsideCorners],
    ['Inside corners', r.insideCorners],
    ['Hidden hangers', r.hangers],
    ['Gutter screws (lbs)', r.screwsLbs],
    ['Splash blocks', r.splashBlocks],
    ['Sealant tubes', r.sealantTubes],
  ] : [];

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Free Gutter Calculator | Gutter Material & Linear Feet Estimator | SquareCount</title>
        <meta name="description" content="Free gutter calculator: enter your gutter linear feet (or house perimeter) and instantly estimate gutter sections, downspouts, elbows, end caps, hangers, and sealant. Then measure gutters automatically from satellite with SquareCount." />
        <meta property="og:title" content="Free Gutter Calculator | SquareCount" />
        <meta property="og:description" content="Estimate gutter sections, downspouts, elbows, hangers, and materials from your linear footage — free." />
        <link rel="canonical" href="https://squarecount.io/tools/gutter-calculator" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebApplication',
          name: 'Gutter Calculator',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Web',
          url: 'https://squarecount.io/tools/gutter-calculator',
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
          description: 'Free gutter material calculator: estimate gutter sections, downspouts, elbows, end caps, hangers, and sealant from linear footage.',
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: [
            { '@type': 'Question', name: 'How do you calculate gutter materials?', acceptedAnswer: { '@type': 'Answer', text: 'Start with total gutter linear feet (the length of the roof eaves). Divide by 10 for the number of 10-ft sections, add one downspout per ~35–40 ft, two elbows per downspout, end caps for each run, and a hidden hanger roughly every 3 feet. This free calculator does it instantly.' } },
            { '@type': 'Question', name: 'How many downspouts do I need?', acceptedAnswer: { '@type': 'Answer', text: 'A common rule is one downspout per 35–40 linear feet of gutter, with a minimum of two per home. This calculator applies that automatically.' } },
            { '@type': 'Question', name: 'How do I measure gutter linear feet?', acceptedAnswer: { '@type': 'Answer', text: 'Gutter linear feet is the total length of the roof eaves (the lower horizontal edges). You can measure it on-site, or measure it automatically from satellite imagery with SquareCount in about 60 seconds.' } },
          ],
        })}</script>
      </Helmet>
      <Navbar />

      <section className="bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-6 py-16">
          <div className="inline-flex items-center gap-2 bg-orange-500/20 border border-orange-500/30 rounded-full px-4 py-2 mb-5">
            <Calculator className="w-4 h-4 text-orange-400" />
            <span className="text-orange-300 text-sm font-medium">Free tool</span>
          </div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tight mb-4">Free Gutter Calculator</h1>
          <p className="text-lg text-slate-300 max-w-2xl">
            Enter your gutter linear feet (or house perimeter) and get an instant material estimate —
            sections, downspouts, elbows, hangers, and more. Want it measured for you?
            SquareCount pulls gutter footage straight from satellite imagery.
          </p>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 py-12">
        <div className="grid md:grid-cols-2 gap-8">
          {/* Input */}
          <div className="bg-slate-50 rounded-2xl p-6 border border-slate-200">
            <div className="flex gap-2 mb-5">
              <button onClick={() => setMode('gutter')} className={`flex-1 py-2 rounded-lg text-sm font-semibold ${mode === 'gutter' ? 'bg-orange-500 text-white' : 'bg-white text-slate-600 border border-slate-200'}`}>Gutter length</button>
              <button onClick={() => setMode('perimeter')} className={`flex-1 py-2 rounded-lg text-sm font-semibold ${mode === 'perimeter' ? 'bg-orange-500 text-white' : 'bg-white text-slate-600 border border-slate-200'}`}>House perimeter</button>
            </div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              {mode === 'gutter' ? 'Total gutter linear feet' : 'House perimeter (ft)'}
            </label>
            <div className="relative">
              <Ruler className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="number" inputMode="decimal" min="0" value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="e.g. 180"
                data-testid="gutter-input"
                className="w-full pl-9 pr-4 py-3 rounded-lg border border-slate-300 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none"
              />
            </div>
            {mode === 'perimeter' && (
              <p className="text-xs text-slate-500 mt-2">We estimate gutters at ~60% of perimeter (eaves only). Gutter length ≈ {Math.round(linearFt)} ft.</p>
            )}
          </div>

          {/* Results */}
          <div className="bg-white rounded-2xl p-6 border border-slate-200">
            <h2 className="font-heading font-bold text-lg text-slate-900 mb-4">Material estimate</h2>
            {r ? (
              <ul className="divide-y divide-slate-100">
                {rows.map(([label, val]) => (
                  <li key={label} className="flex justify-between py-2 text-sm">
                    <span className="text-slate-600">{label}</span>
                    <span className="font-semibold text-slate-900">{val}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-slate-400 text-sm">Enter a value to see your gutter material estimate.</p>
            )}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-10 bg-slate-900 rounded-2xl p-8 text-center text-white">
          <h2 className="font-heading font-black text-2xl mb-3">Skip the tape measure</h2>
          <p className="text-slate-300 mb-6 max-w-2xl mx-auto">
            SquareCount measures gutter linear feet from satellite imagery in about 60 seconds — alongside the full roof —
            so you can quote roofs and gutters without leaving your desk. Free to start.
          </p>
          <button onClick={() => openAuthModal('signup')} className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-400 text-white font-heading font-bold tracking-wide px-8 py-3 rounded-lg">
            Measure Gutters Free <ArrowRight className="w-5 h-5" />
          </button>
          <p className="text-xs text-slate-400 mt-3">
            <Link to="/gutter-measurement" className="underline hover:text-slate-200">Learn about satellite gutter measurement →</Link>
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
